import React from "react";
import { Box, Typography, Divider, Alert } from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import AppCircularProgress from "../common/AppCircularProgress";
import { NewsletterResponseDto } from "../../shared/dtos/newsletter/NewsletterResponseDto";
import { formatDateForDisplay } from "../../helpers/dateHelpers";

interface ViewNewsletterModalProps {
  open: boolean;
  onClose: () => void;
  newsletter: NewsletterResponseDto | null;
  loading: boolean;
  error?: string | null;
}

const ViewNewsletterModal: React.FC<ViewNewsletterModalProps> = ({
  open,
  onClose,
  newsletter,
  loading,
  error = null,
}) => {
  const renderContent = (): React.ReactNode => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 5,
          }}
        >
          <AppCircularProgress />
          <Typography sx={{ ml: 2, color: "text.secondary" }}>
            Loading Content...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!newsletter) {
      return (
        <Typography sx={{ my: 2, color: "text.secondary" }}>
          No content available.
        </Typography>
      );
    }

    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          {newsletter.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", mb: 2 }}
        >
          Published: {formatDateForDisplay(newsletter.publicationDate)} • By{" "}
          {newsletter.createdByName || "School Administration"}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{
            p: 2,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            maxHeight: 300,
            overflowY: "auto",
          }}
        >
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {newsletter.bodyContent}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem
            label="Academic Year"
            value={newsletter.academicYearName || "N/A"}
          />
          <DetailItem
            label="Term"
            value={newsletter.termName || "General / Whole Year"}
          />
          <DetailItem
            label="Campus / Branch"
            value={newsletter.branchName || "All Campuses"}
          />
          <DetailItem
            label="Grade Level"
            value={newsletter.gradeLevelName || "All Grades"}
          />
          <DetailItem
            label="Target Audience Scope"
            value={
              newsletter.audienceScope
                ? String(newsletter.audienceScope).replace(/_/g, " ")
                : "All"
            }
          />
        </Box>
      </Box>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Newsletter Details"
      width="md"
    >
      {renderContent()}
    </AppModal>
  );
};

export default ViewNewsletterModal;
